import { api } from '@smena/ui';

export type AutonomyPreset = 'strict' | 'balance' | 'free';
export type AutonomyMode = 'auto' | 'auto_notify' | 'confirm' | 'off';
export type ToolClass = 'read' | 'outbound' | 'irreversible';

export interface AutonomyRuleView {
  id: string;
  scope: 'tool' | 'intent';
  key: string;
  mode: AutonomyMode;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutonomyToolView {
  name: string;
  /** Русское название инструмента для UI. */
  title: string;
  /** Одно предложение-описание для tooltip'а. */
  description: string;
  class: ToolClass;
  classLabel: string;
  classHint: string;
  /** Приходит с сервера, но UI на это поле не опирается: область он считает сам
   *  (см. AgentPage + @smena/core/labels), иначе таблица пустеет на старом API. */
  area?: string;
  areaLabel?: string;
  mode: AutonomyMode;
  modeLabel: string;
  modeHint: string;
  reason: 'override' | 'preset' | 'unknown_tool';
  reasonLabel: string;
}

export interface AutonomyIntentView {
  name: string;
  title: string;
  description: string;
  area?: string;
  areaLabel?: string;
  /** Тема, которую сервер всегда уводит человеку (ADR-6) - правило для неё не задаётся. */
  hardStop?: boolean;
  /** Что мешает отдать тему на автомат. null - ничего не мешает. */
  blocker?: string | null;
  /** null - override не задан (работает session mode). Иначе - активный режим intent-override'а. */
  mode: AutonomyMode | null;
}

export interface AutonomyStateView {
  preset: AutonomyPreset;
  presetMatrix: Record<AutonomyPreset, Record<ToolClass, AutonomyMode>>;
  presets: AutonomyPreset[];
  rules: AutonomyRuleView[];
  tools: AutonomyToolView[];
  intents: AutonomyIntentView[];
  presetLabels: Record<AutonomyPreset, string>;
  presetHints: Record<AutonomyPreset, string>;
  modeLabels: Record<AutonomyMode, string>;
  modeHints: Record<AutonomyMode, string>;
}

export function fetchAutonomyState(): Promise<AutonomyStateView> {
  return api('/api/staff/autonomy/state');
}

export function setAutonomyPreset(preset: AutonomyPreset): Promise<{ preset: AutonomyPreset }> {
  return api('/api/staff/autonomy/preset', { method: 'PATCH', json: { preset } });
}

export function upsertAutonomyRule(input: {
  scope: 'tool' | 'intent';
  key: string;
  mode: AutonomyMode;
}): Promise<AutonomyRuleView> {
  return api('/api/staff/autonomy/rules', { method: 'PUT', json: input });
}

export function deleteAutonomyRule(scope: 'tool' | 'intent', key: string): Promise<{ ok: true }> {
  return api(`/api/staff/autonomy/rules/${scope}/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export interface PendingActionView {
  id: string;
  sid: string;
  channelRef: string | null;
  toolName: string;
  toolCall: Record<string, unknown>;
  status: 'pending' | 'executed' | 'rejected' | 'expired';
  expiresAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
}

export function fetchPendingActions(status?: string): Promise<{ items: PendingActionView[] }> {
  const q = status ? `?status=${status}` : '';
  return api(`/api/staff/pending-actions${q}`);
}

export function executePendingAction(id: string): Promise<PendingActionView> {
  return api(`/api/staff/pending-actions/${id}/execute`, { method: 'POST', json: {} });
}

export function rejectPendingAction(id: string, reason?: string): Promise<PendingActionView> {
  return api(`/api/staff/pending-actions/${id}/reject`, { method: 'POST', json: { reason } });
}

/** Готовность аккаунта к автоответам - основание для напоминания «пора отпускать агента». */
export interface AutonomyReadiness {
  draftOnly: boolean;
  firstReplyAt: string | null;
  daysObserved: number;
  confirmedCount: number;
  minDays: number;
  minConfirmed: number;
  ready: boolean;
}

export function fetchAutonomyReadiness(): Promise<AutonomyReadiness> {
  return api('/api/staff/autonomy/readiness');
}
